import { useCallback, useMemo } from 'react'
import { documentationRootGuid } from '@constants'
import Edit from '@mui/icons-material/Edit'
import { Box, Button, Divider, Stack } from '@mui/material'
import { Form2, MarkdownEditor } from '@ui/forms'
import { AttachmentEditing } from '../../../../types'
import BaseFormFields from '../../../BaseFormFields'
import {
  createPageSelectionSchema,
  formatPageSelection,
  withParsedPages,
} from '../../../PageSelectionField/utils'
import TemplateFileTabs from '../../../TemplateFileTabs'
import AttachmentSection from './components/AttachmentSection'

const toMargin = (value: unknown) => Number(value) || 0

type DefaultValues = MarkdownFileItemConfig & {
  // Page selection of an attached PDF as edited text, stored as `pages`
  page_selection: string
}

interface Props {
  item: FileItem
  mutate: (data: MarkdownFileItemConfig) => void
  attachmentEditing: AttachmentEditing
  onCropConfigChange: (config: {
    croppedLeft: number
    croppedTop: number
    croppedRight: number
    croppedBottom: number
  }) => void
  /** Page count of the attached file shown in the viewer, if loaded */
  pageCount?: number
  /** Called with the valid page selection while it is edited, before it is saved */
  onPagesChange?: (itemGuid: string, pages: number[] | null) => void
}

const MarkdownFileProperties = ({
  item,
  mutate,
  attachmentEditing,
  onCropConfigChange,
  pageCount,
  onPagesChange,
}: Props) => {
  const defaultValues = useMemo(() => {
    const baseConfig: DefaultValues = {
      ...(item.additional_data ?? {}),
      item_guid: item.guid,
      name: item.name,
      content: item?.additional_data?.content ?? '',
      bu: item?.additional_data?.bu ?? 0,
      bb: item?.additional_data?.bb ?? 0,
      bl: item?.additional_data?.bl ?? 0,
      br: item?.additional_data?.br ?? 0,
      page_selection: formatPageSelection(item?.additional_data?.pages),
    }

    return baseConfig
  }, [item])

  const validationSchema = useMemo(
    () => createPageSelectionSchema(pageCount, defaultValues.page_selection),
    [pageCount, defaultValues.page_selection],
  )

  const onSubmit = useCallback(
    (data: DefaultValues) => {
      // A cleared margin field holds '' (UnitField), which the backend rejects as a number
      const margins = {
        bu: toMargin(data.bu),
        bb: toMargin(data.bb),
        bl: toMargin(data.bl),
        br: toMargin(data.br),
      }
      // Live preview of the crop margins on the attached PDF ("Datei" tab)
      onCropConfigChange({
        croppedLeft: margins.bl,
        croppedTop: margins.bu,
        croppedRight: margins.br,
        croppedBottom: margins.bb,
      })
      const config = withParsedPages({ ...data, ...margins })
      onPagesChange?.(item.guid, config.pages)
      mutate(config)
    },
    [mutate, onCropConfigChange, onPagesChange, item.guid],
  )

  // With a file attached, the page and the file are separate tabs. The viewer
  // follows the tab (preview of the page / the file itself)
  const hasAttachment = !!item.attachment
  const showTemplateFields = !hasAttachment || !attachmentEditing.isShowingFile
  const showFileFields = !hasAttachment || attachmentEditing.isShowingFile
  const isWordAttachment = hasAttachment && item.attachment?.mimetype !== 'application/pdf'

  return (
    <Stack>
      <Form2
        key={JSON.stringify(defaultValues)}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
        validationSchema={validationSchema}
        submitOnChange
      >
        <Stack spacing={2}>
          {hasAttachment && (
            <TemplateFileTabs
              isShowingFile={attachmentEditing.isShowingFile}
              onShowFileChange={attachmentEditing.onShowFileChange}
            />
          )}

          <Box p={1}>
            {showTemplateFields && (
              <>
                <BaseFormFields
                  elementGuid={item.element_guid}
                  showBeginOnNewPage={item.parent_guid !== documentationRootGuid}
                />

                <Divider />

                <MarkdownEditor name="content" allowHorizontalResize={false} label="Inhalt" />

                {isWordAttachment && (
                  <Stack direction="row">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={() => attachmentEditing.onShowFileChange(true)}
                    >
                      Datei bearbeiten
                    </Button>
                  </Stack>
                )}
              </>
            )}

            {!hasAttachment && <Divider />}

            {showFileFields && (
              <AttachmentSection
                item={item}
                attachmentEditing={attachmentEditing}
                pageCount={pageCount}
              />
            )}
          </Box>
        </Stack>
      </Form2>
    </Stack>
  )
}

export default MarkdownFileProperties
